
import { motion, AnimatePresence } from "framer-motion";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

interface LogoAnimationProps {
  onLaunch: (launching: boolean) => void;
}

export const LogoAnimation = ({ onLaunch }: LogoAnimationProps) => {
  const [isLaunching, setIsLaunching] = useState(false);
  const [clickCount, setClickCount] = useState(0);
  const [showCount, setShowCount] = useState(false);
  const [isRolling, setIsRolling] = useState(false);
  const navigate = useNavigate();

  // Generate random particles for the rocket exhaust effect
  const particles = Array.from({ length: 15 }).map((_, i) => ({
    id: i,
    angle: ((i * 24) * Math.PI) / 180,
    scale: Math.random() * 0.5 + 0.5,
    duration: Math.random() * 0.5 + 0.5,
  }));

  const handleLaunch = () => {
    if (isLaunching) {
      const newCount = clickCount + 1;
      setClickCount(newCount);
      setShowCount(true);
      setTimeout(() => setShowCount(false), 1000);

      if (newCount === 5) {
        setIsRolling(true);
        setTimeout(() => {
          navigate('/easter-egg');
          setClickCount(0);
          setIsRolling(false);
        }, 2000);
      }
      return;
    }

    setIsLaunching(true);
    onLaunch(true);
    setTimeout(() => {
      setIsLaunching(false);
      onLaunch(false);
      setClickCount(prevCount => {
        setShowCount(true);
        setTimeout(() => {
          setShowCount(false);
          setClickCount(0);
        }, 1000);
        return -prevCount;
      });
    }, 1000);
  };

  return (
    <div className="relative">
      <motion.div
        className={`relative w-12 h-12 cursor-pointer group`}
        animate={
          isRolling
            ? {
                y: [0, window.innerHeight],
                rotate: [0, 720],
                transition: {
                  y: { duration: 1, ease: "easeIn" },
                  rotate: { duration: 2, ease: [0.45, 0, 0.55, 1] },
                },
              }
            : {
                rotate: isLaunching ? [0, 360] : [0, 360],
                y: isLaunching ? -500 : 0,
                scale: isLaunching ? [1, 1.1, 1] : 1,
              }
        }
        transition={{
          rotate: {
            duration: 3,
            repeat: Infinity,
            ease: "linear"
          },
          y: {
            duration: 0.75,
            ease: "easeIn",
          },
          scale: {
            duration: 0.3,
            times: [0, 0.5, 1]
          }
        }}
        onClick={handleLaunch}
      >
        {/* TON 618-inspired outer ring */}
        <motion.div 
          className="absolute inset-0 rounded-full bg-gradient-to-r from-[#00F0FF] to-[#0EA5E9] opacity-50"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.5, 0.3, 0.5],
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "linear"
          }}
        />
        
        {/* Inner black hole effect */}
        <motion.div 
          className="absolute inset-0 rounded-full bg-black border-2 border-[#00F0FF] shadow-lg shadow-[#00F0FF]/50"
          style={{
            background: "radial-gradient(circle at center, #000000 60%, #0EA5E9 100%)",
          }}
        />
      </motion.div>
      
      <AnimatePresence>
        {showCount && (
          <motion.div
            initial={{ opacity: 0, y: 0, scale: 0.5 }}
            animate={{ opacity: 1, y: -20, scale: 1 }}
            exit={{ opacity: 0, y: -40, scale: 0.5 }}
            className="absolute top-0 left-1/2 transform -translate-x-1/2 text-flux-cyan font-bold text-xl"
          >
            {clickCount > 0 ? `+${clickCount}` : clickCount}
          </motion.div>
        )}

        {isLaunching && (
          <>
            {particles.map((particle) => (
              <motion.div
                key={particle.id}
                className="absolute top-1/2 left-1/2 w-2 h-2 rounded-full bg-gradient-to-t from-[#00F0FF] to-[#0EA5E9]"
                initial={{ scale: 0, x: 0, y: 0, opacity: 1 }}
                animate={{
                  scale: particle.scale,
                  x: Math.cos(particle.angle) * 30,
                  y: Math.sin(particle.angle) * 30 + 20,
                  opacity: 0,
                }}
                exit={{ scale: 0 }}
                transition={{
                  duration: particle.duration,
                  ease: "easeOut",
                  repeat: 3,
                }}
              />
            ))}
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

